import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { useNavigate } from 'react-router-dom';

import { DOCUMENT_TYPES, PAGE_SIZE_SM } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useSearch } from '~libs/searchParams';
import { useToast } from '~libs/toast';

import { Button, ClickableIcon, ErrorResponse, Spinner, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faMagnifyingGlass } from '~components/icon';
import { ColumnPickerModal, TableView, useColumnVisibility } from '~components/table-view';

import { COLUMN_GROUPS, columnsFactory, exportColumnsFor } from '~support-at-home/forms/dashboard/claimItemsDashboardList';
import { searchFields } from '~support-at-home/forms/dashboard/claimItemsDashboardSearch';

import { useGetSahClaimStatusesQuery } from '~lookups/actions/sahClaimStatuses';
import { useGetSahServicesQuery } from '~lookups/actions/sahServices';
import { getAll as getClients } from '~main/actions/clients';
import { CreateDocumentBatchJob, useCreateDocumentBatchMutation } from '~main/actions/documentBatches';
import {
  useGetDashboardClaimItemsQuery,
  useGetDashboardClaimItemsSpecsQuery,
} from '~support-at-home/actions/dashboardClaimItems';

// Identifies this table's saved column preference row — must match tp-assist-api's table_key.
const TABLE_KEY = 'sah-claim-items-dashboard';

export const ClaimItemsDashboard: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [createDocumentBatch, { isLoading: isGenerating }] = useCreateDocumentBatchMutation();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearch('claim-items-dashboard-search', {});

  const search = useCallback(
    (values: Record<string, any>) => {
      setSearchParams(values);
      setCurrentPage(1);
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (isActive) {
      dispatch(getClients({ disabled: undefined, attributes: ['id', 'full_name', 'non_chargeable', 'disabled'] }));
    }
  }, [isActive]);

  const queryArgs = useMemo(
    () => ({
      ...searchParams,
      limit: PAGE_SIZE_SM,
      offset: (currentPage - 1) * PAGE_SIZE_SM,
    }),
    [currentPage, searchParams],
  );

  const { data: claimStatuses = [] } = useGetSahClaimStatusesQuery(undefined, { skip: !isActive });
  const { data: sahServices = [] } = useGetSahServicesQuery(undefined, { skip: !isActive });
  const { data, isLoading, isFetching, isError, error } = useGetDashboardClaimItemsQuery(queryArgs, {
    skip: !isActive,
  });
  const {
    data: specsData,
    isLoading: isLoadingSpecs,
    isError: isErrorSpecs,
    error: errorSpecs,
    refetch: refetchSpecs,
  } = useGetDashboardClaimItemsSpecsQuery(undefined, { skip: !isActive });
  const {
    isEnabled: isColumnPickerEnabled,
    visibleColumnIds,
    setVisibleColumnIds,
  } = useColumnVisibility({
    tableKey: TABLE_KEY,
    columnSpecs: specsData?.columns,
    onPreferencesChanged: refetchSpecs,
    onError: (columnPreferenceError) => {
      const message = columnPreferenceError instanceof Error ? columnPreferenceError.message : 'Failed to save column preferences';
      showToast('danger', message);
    },
  });
  const [isColumnPickerOpen, setIsColumnPickerOpen] = useState(false);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleNavigateToClaim = useCallback(
    (claimId: string) => {
      navigate(`/support-at-home/claims/${claimId}`);
    },
    [navigate],
  );

  // Column existence, labels, and current visibility are entirely driven by the specs response
  // (the backend already resolves visibility per-user) — only per-column rendering behaviour
  // (links, formatting, navigation) is declared on the frontend, in columnsFactory.
  const columns = useMemo(
    () => columnsFactory(specsData?.columns ?? [], handleNavigateToClaim),
    [specsData, handleNavigateToClaim],
  );

  const onGenerateCsv = async () => {
    try {
      const job = {
        document_type: DOCUMENT_TYPES.SAH_CLAIM_ITEMS.CSV,
        ...searchParams,
        columns: exportColumnsFor(columns),
      } as CreateDocumentBatchJob & Record<string, unknown>;
      await createDocumentBatch([job]).unwrap();
      showToast('success', 'CSV generation started');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start CSV generation';
      showToast('danger', message);
    }
  };

  // A specs response that resolved successfully but carries no `columns` array (e.g. an old
  // backend pod mid-rollout that doesn't recognise `specs=true` yet, silently returning the plain
  // row-data shape with HTTP 200 instead) is indistinguishable from a real error to the caller —
  // treat it as one rather than rendering a table collapsed to a single Origin Link column.
  const hasIncompleteSpecs = isActive && !isLoadingSpecs && !isErrorSpecs && !specsData?.columns;

  const renderContent = () => {
    if (isLoading || isLoadingSpecs) {
      return (
        <div className="loading" role="status" aria-live="polite">
          <Spinner loading={true} type="beat" size={10} />
          Loading claim items...
        </div>
      );
    }

    if (isError || isErrorSpecs || hasIncompleteSpecs) {
      const message = hasIncompleteSpecs
        ? 'Failed to load claim items - column data was unavailable'
        : `Failed to load claim items - ${getErrorMessage(error ?? errorSpecs)}`;
      return <ErrorResponse message={message} dataCy="claim-items-dashboard-error" />;
    }

    return (
      <TableView
        id="claim-items-table"
        columns={columns}
        data={data?.rows ?? []}
        isLoading={isFetching}
        emptyMessage="No claim items found for selected filters"
        allowPagination
        paginationType="pages"
        showCount
        currentPage={currentPage}
        pageSize={PAGE_SIZE_SM}
        totalCount={data?.total ?? 0}
        onPageChange={handlePageChange}
        readonly
        bordered
      />
    );
  };

  return (
    <div
      className={`claim-items-dashboard${isFetching ? ' fetching' : ''}`}
      data-cy="claim-items-dashboard"
      aria-busy={isFetching}
    >
      <Toolbar
        id="claim-items-dashboard"
        rightCluster={
          <>
            <ClickableIcon
              className="me-2"
              icon={faMagnifyingGlass}
              id="claim-items-search-button"
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
              aria-expanded={searchBarOpen}
              aria-controls="claim-items-searchbar"
            />
            <Button
              size="sm"
              color="secondary"
              loading={isGenerating}
              disabled={isGenerating || isLoadingSpecs || isErrorSpecs || hasIncompleteSpecs}
              onClick={onGenerateCsv}
            >
              Generate CSV
            </Button>
            {isColumnPickerEnabled && (
              <Button
                size="sm"
                color="secondary"
                disabled={isLoadingSpecs || isErrorSpecs || hasIncompleteSpecs}
                onClick={() => setIsColumnPickerOpen(true)}
                dataCy="claim-items-dashboard-column-picker-button"
              >
                Columns
              </Button>
            )}
          </>
        }
      />
      <SearchBar
        id="claim-items-searchbar"
        isOpen={searchBarOpen}
        fields={searchFields(claimStatuses, sahServices)}
        initialValues={searchParams}
        onSubmit={search}
        storageKey="claim-items-dashboard"
      />
      {renderContent()}
      {isColumnPickerEnabled && (
        <ColumnPickerModal
          isOpen={isColumnPickerOpen}
          setIsOpen={setIsColumnPickerOpen}
          columns={specsData?.columns ?? []}
          groups={COLUMN_GROUPS}
          visibleColumnIds={visibleColumnIds}
          onApply={setVisibleColumnIds}
          dataCy="claim-items-dashboard-column-picker"
        />
      )}
    </div>
  );
};
